import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BudgetCategory,
  BudgetContingency,
  BudgetI,
  BudgetSubCategory,
  BudgetTotal,
  BudgetUpdate,
} from "./types";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  CellClickedEvent,
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
  RowClassParams,
  ValueSetterParams,
} from "ag-grid-community";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";
import {
  getBudgetContingencyVariance,
  getBudgetEstimateContingency,
  getBudgetItemsActual,
  getBudgetCategoryActual,
  getBudgetCategoryEstimate,
  getBudgetCategoryVariance,
  getBudgetItemsEstimate,
  getBudgetItemsVariance,
  getBudgetTotalsFromGrid,
  instanceOfBudgetContingency,
  toNullableBudgetNumber,
} from "./BudgetItemUtils";
import { categoryFringesSearchParamName } from "./Budget";
import { MAX_INPUT_NUMBER_VALUE } from "./DetailedLevelBudgetGrid";
import { UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { ContingencyAdd } from "./ContingencyAdd";
import { budgetNonEditableReasons } from "config";
import { formatNumberWithCommas } from "utils";
import { defineSessionRecordSaves } from "lib/record-save-session";
import { useRecordSaveView } from "hooks/useRecordSaveView";
import { budgetKeys } from "queries";
import { useBudgetMutations } from "hooks/useBudget";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import {
  AccountNumberMaxWidth,
  baseHeaderClasses,
  budgetGridLinkClass,
  budgetMonetaryCellStyle,
  topSheetDefaultColDef,
  isActiveGridApi,
  RIVET_BUDGET_GRID_CLASS,
  RIVET_BUDGET_TOP_SHEET_GRID_CLASS,
  RIVET_BUDGET_TOP_SHEET_GRID_MARKER_CLASS,
  RIVET_BUDGET_TOP_SHEET_WRAPPER_CLASS,
} from "./BudgetGridCommon";
import {
  budgetActualAmountClass,
  budgetEstimateAmountClass,
  budgetMonetaryCellClass,
  budgetVarianceAmountClass,
} from "./budgetGridFormatting";
import { CellRendererBase } from "./CellRendererBase";
import {
  getBudgetCategoryColor,
  getBudgetRowHoverTint,
  getBudgetRowTint,
  getBudgetSubRowHoverTint,
} from "./budgetCategoryPalette";
import ImportanceScoreEditor from "./cellRenderers/ImportanceScoreEditor";
import { GridHeaderWithTooltip } from "./GridHeaderWithTooltip";
import { ToggleLock } from "components/common/toggle/ToggleLock";
import { AgGridLoaderOverlay } from "./AgGridLoaderOverlay";
import { InputV3 } from "components/common/input/InputV3";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import { ChevronDown, ChevronRight, FoldVertical, Plus, UnfoldVertical } from "lucide-react";
import type { BudgetTopSheetViewMode } from "./budgetTopSheetViewMode";
import {
  buildTopSheetBodyRows,
  buildTopSheetPinnedRows,
  getTopSheetCategoryIds,
  instanceOfBudgetCategoryFringes,
  type BudgetCategoryFringes,
} from "./topSheetRows.utils";
import { TopSheetRowActions } from "./TopSheetRowActions";
import ConfirmationModal from "components/common/modals/confirmation/ConfirmationModal";
import { FaTrash } from "react-icons/fa";
import {
  buildCategoryReorderPayload,
  buildSubCategoryReorderPayload,
  canMoveBudgetCategoryDown,
  canMoveBudgetCategoryUp,
  canMoveBudgetSubCategoryDown,
  canMoveBudgetSubCategoryUp,
  moveBudgetCategory,
  moveBudgetSubCategory,
} from "./budgetStructureReorder";
import { suggestNextSubCategoryAccountNumber } from "./budgetStructureAccountNumber";
import {
  collectBudgetAccountNumbers,
  getBudgetAccountNumberFormatError,
  normalizeBudgetAccountNumber,
} from "./budgetAccountNumber.validation";
import {
  focusTopSheetStructureRename,
  type TopSheetStructureEditTarget,
} from "./topSheetStructureEdit";
import { TABLE_ROW_HEIGHT } from "components/layout/table/tableStandard";

type TopSheetCellActions = {
  handleAddSubCategory: (categoryId: number) => void;
  handleMoveCategory: (categoryId: number, direction: -1 | 1) => void;
  handleMoveSubCategory: (subCategoryId: number, direction: -1 | 1) => void;
  startStructureRename: (kind: TopSheetStructureEditTarget["kind"], id: number) => void;
  openDeleteConfirmation: (kind: "category" | "subcategory", id: number, name: string) => void;
  onTopSheetClick: (id: number) => void;
  onCategoryFringesClick: (id: number) => void;
  toggleCategoryCollapsed: (categoryId: number) => void;
  updateContingencyLock: (locked: boolean) => void;
};

export type { BudgetCategoryFringes } from "./topSheetRows.utils";
export { instanceOfBudgetCategoryFringes } from "./topSheetRows.utils";
type SheetCategoryFields = Pick<BudgetCategory, "name">;
type SheetSubCategoryFields = Pick<BudgetSubCategory, "name" | "account_number">;

// One queue per kind of record for the whole app, so a reopened top sheet shares it.
const contingencySaves = defineSessionRecordSaves<BudgetUpdate>();
const categorySaves = defineSessionRecordSaves<SheetCategoryFields>();
const subCategoryStructureSaves = defineSessionRecordSaves<SheetSubCategoryFields>();

interface TopSheetBudgetGridProps {
  sheetData?: BudgetCategory[];
  budgetId?: string;
  setSheetData: (newData: BudgetCategory[]) => void;
  updateBudgetDetail: (newData: Partial<BudgetI>) => void;
  budgetDetail?: Omit<BudgetI, "categories">;
  topSheetGridApi?: GridApi;
  setTopSheetGridApi: (api: GridApi | undefined) => void;
  gridRef: React.RefObject<AgGridReact<any>> | null;
  viewMode: BudgetTopSheetViewMode;
  updateBudget: UseMutationResult<
    any,
    unknown,
    {
      update_data: Partial<BudgetUpdate>;
    },
    unknown
  >;
}

enum GridColumnIdentifiers {
  AccountNumber = "account_number",
  Description = "description",
  Estimate = "estimate",
  ImportanceScore = "importance_score",
  Actual = "actual",
  Variance = "variance",
  RowActions = "row_actions",
}

const TOP_SHEET_MONETARY_COLUMN_WIDTH = 132;

const TOP_SHEET_ROW_HEIGHT = TABLE_ROW_HEIGHT;

export const instanceOfBudgetSubCategory = (object: any): object is BudgetSubCategory => {
  return "budget_sub_category_id" in object;
};

const instanceOfBudgetTotal = (object: any): object is BudgetTotal => {
  return "estimate" in object && !("nested_fringes" in object);
};

const instanceOfBudgetCategory = (object: any): object is BudgetCategory => {
  return "sub_categories" in object;
};

const isTopSheetChildRow = (data: unknown): boolean =>
  instanceOfBudgetSubCategory(data) || instanceOfBudgetCategoryFringes(data);

const getTopSheetRowId = (params: GetRowIdParams): string => {
  const data = params.data;
  if (instanceOfBudgetCategory(data)) return `category-${data.budget_category_id}`;
  if (instanceOfBudgetSubCategory(data)) return `subcategory-${data.budget_sub_category_id}`;
  if (instanceOfBudgetCategoryFringes(data)) return `fringes-${data.category_id}`;
  if (instanceOfBudgetContingency(data)) return "contingency";
  return "grand-total";
};

const isTopSheetCategoryRow = (params: RowClassParams): boolean =>
  !!params.data &&
  typeof params.data === "object" &&
  "budget_category_id" in params.data &&
  !("items" in params.data);

const getTopSheetChildRowCategoryColorIndex = (
  sheetData: BudgetCategory[] | undefined,
  data: unknown
): number => {
  if (instanceOfBudgetCategoryFringes(data)) {
    return (
      sheetData?.findIndex((category) => category.budget_category_id === data.category_id) ?? -1
    );
  }
  if (instanceOfBudgetSubCategory(data)) {
    return (
      sheetData?.findIndex((category) =>
        category.sub_categories.some(
          (subCategory) => subCategory.budget_sub_category_id === data.budget_sub_category_id
        )
      ) ?? -1
    );
  }
  return -1;
};

const getTopSheetRowClass = (params: RowClassParams): string => {
  if (isTopSheetCategoryRow(params)) {
    return "text-foreground font-semibold budget-category-row cursor-pointer";
  }
  if (isTopSheetChildRow(params.data)) {
    return "bg-transparent text-foreground topsheet-child-row";
  }
  return "bg-transparent text-foreground";
};

const getTopSheetRowStyle = (
  sheetData: BudgetCategory[] | undefined,
  params: RowClassParams
): Record<string, string> | undefined => {
  if (!params.data) return undefined;

  if (isTopSheetCategoryRow(params)) {
    const categoryId = (params.data as BudgetCategory).budget_category_id;
    const colorIndex =
      sheetData?.findIndex((category) => category.budget_category_id === categoryId) ?? 0;
    const accentColor = getBudgetCategoryColor(colorIndex);
    return {
      "backgroundColor": getBudgetRowTint(colorIndex),
      "boxShadow": `inset 3px 0 0 0 ${accentColor}`,
      "--budget-row-hover": getBudgetRowHoverTint(colorIndex),
    };
  }

  if (isTopSheetChildRow(params.data)) {
    const colorIndex = getTopSheetChildRowCategoryColorIndex(sheetData, params.data);
    if (colorIndex < 0) return undefined;
    return {
      "--budget-row-hover": getBudgetSubRowHoverTint(colorIndex),
    };
  }

  return undefined;
};

export const TopSheetBudgetGrid: React.FC<TopSheetBudgetGridProps> = ({
  sheetData,
  budgetId,
  setSheetData,
  updateBudgetDetail,
  budgetDetail,
  updateBudget,
  topSheetGridApi,
  setTopSheetGridApi,
  gridRef,
  viewMode,
}) => {
  const { projectId, documentId } = useProjectIdentifiers();

  const { isViewer } = useProjectAuth(projectId);
  const budgetLocked = budgetDetail?.locked === true;
  const canEditStructure = !isViewer && !budgetLocked;
  const structureDisabledTitle = isViewer
    ? budgetNonEditableReasons.viewer
    : budgetLocked
      ? budgetNonEditableReasons.budgetLocked
      : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const [quickFilter, setQuickFilter] = useState("");
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<number[]>([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    kind?: "category" | "subcategory";
    id?: number;
    name?: string;
  }>({ open: false });
  const collapsedCategoryIdsRef = useRef<number[]>([]);
  const collapsedInitializedForBudgetRef = useRef<string | undefined>(undefined);
  const viewModeRef = useRef<BudgetTopSheetViewMode>(viewMode);
  collapsedCategoryIdsRef.current = collapsedCategoryIds;
  viewModeRef.current = viewMode;
  const canEditStructureRef = useRef(canEditStructure);
  canEditStructureRef.current = canEditStructure;
  const structureDisabledTitleRef = useRef(structureDisabledTitle);
  structureDisabledTitleRef.current = structureDisabledTitle;
  const sheetDataRef = useRef(sheetData);
  sheetDataRef.current = sheetData;
  // Sheet edits save one at a time per record, through queues shared by every top sheet, and
  // only touch the sheet that is open on the budget they were made in.
  const queryClient = useQueryClient();
  const sheetScopeRef = useRef("");
  sheetScopeRef.current = `${projectId}/${documentId}/${budgetId ?? ""}`;
  const saveView = useRecordSaveView(sheetScopeRef.current);
  const budgetDetailRef = useRef(budgetDetail);
  budgetDetailRef.current = budgetDetail;
  const topSheetGridApiRef = useRef(topSheetGridApi);
  topSheetGridApiRef.current = topSheetGridApi;
  const setSubCategoryLockedRef = useRef<(budgetSubCategoryId: number, locked: boolean) => void>(
    () => undefined
  );
  const pendingStructureEditRef = useRef<TopSheetStructureEditTarget | null>(null);
  const cellActionsRef = useRef<TopSheetCellActions>({
    handleAddSubCategory: () => undefined,
    handleMoveCategory: () => undefined,
    handleMoveSubCategory: () => undefined,
    startStructureRename: () => undefined,
    openDeleteConfirmation: () => undefined,
    onTopSheetClick: () => undefined,
    onCategoryFringesClick: () => undefined,
    toggleCategoryCollapsed: () => undefined,
    updateContingencyLock: () => undefined,
  });

  const toggleCategoryCollapsed = useCallback((categoryId: number) => {
    setCollapsedCategoryIds((current) =>
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId]
    );
  }, []);

  const handleTopSheetCellClicked = useCallback(
    (event: CellClickedEvent) => {
      if (event.column.getColId() === GridColumnIdentifiers.RowActions) return;
      if (!instanceOfBudgetCategory(event.data)) return;

      const isEditingRow = event.api
        .getEditingCells()
        .some((cell) => cell.rowIndex === event.rowIndex);
      if (isEditingRow) return;

      toggleCategoryCollapsed(event.data.budget_category_id);
    },
    [toggleCategoryCollapsed]
  );

  const subCategorySearchParamName = "subCategory";

  const onTopSheetClick = (id: any) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(subCategorySearchParamName, id);
    setSearchParams(newSearchParams, { replace: true });
  };
  useEffect(() => {
    if (
      budgetId &&
      sheetData &&
      sheetData.length > 0 &&
      collapsedInitializedForBudgetRef.current !== budgetId
    ) {
      setCollapsedCategoryIds([]);
      collapsedInitializedForBudgetRef.current = budgetId;
    }
  }, [budgetId, sheetData]);

  const bodyRows = useMemo(() => {
    if (!sheetData) return null;
    return buildTopSheetBodyRows({
      sheetData,
      collapsedCategoryIds,
      quickFilter,
      viewMode,
    });
  }, [sheetData, collapsedCategoryIds, quickFilter, viewMode]);

  const pinnedRows = useMemo(() => {
    if (!sheetData || !budgetDetail || !bodyRows?.length) return [];
    return buildTopSheetPinnedRows(sheetData, budgetDetail, bodyRows);
  }, [
    sheetData,
    budgetDetail?.contingency_percentage,
    budgetDetail?.contingency_locked,
    budgetDetail?.contingency_actual,
    bodyRows,
  ]);

  const getRowStyle = useCallback(
    (params: RowClassParams) => getTopSheetRowStyle(sheetDataRef.current, params),
    []
  );

  useEffect(() => {
    if (!isActiveGridApi(topSheetGridApi)) return;
    topSheetGridApi.refreshCells({
      // Description holds the contingency lock, which also depends on canEditStructure.
      columns: [GridColumnIdentifiers.RowActions, GridColumnIdentifiers.Description],
      force: true,
    });
  }, [canEditStructure, topSheetGridApi]);

  // Account lock icons sit in cells whose values do not change with the lock, so AG Grid keeps
  // them when new sheet data arrives. Redraw them so a lock restored after a failed save shows.
  useEffect(() => {
    if (!isActiveGridApi(topSheetGridApi)) return;
    topSheetGridApi.refreshCells({ columns: [GridColumnIdentifiers.AccountNumber], force: true });
  }, [sheetData, topSheetGridApi]);

  useEffect(() => {
    return () => {
      setTopSheetGridApi(undefined);
    };
  }, [setTopSheetGridApi]);

  // Reserve the row-actions column for non-viewers so locking never adds/removes it.
  useEffect(() => {
    if (!isActiveGridApi(topSheetGridApi)) return;
    topSheetGridApi.setColumnsVisible([GridColumnIdentifiers.RowActions], !isViewer);
  }, [topSheetGridApi, isViewer]);

  const allTopSheetCategoryIds = sheetData ? getTopSheetCategoryIds(sheetData) : [];
  const allBucketsCollapsed =
    allTopSheetCategoryIds.length > 0 &&
    allTopSheetCategoryIds.every((id) => collapsedCategoryIds.includes(id));

  const handleToggleAllBuckets = () => {
    if (allBucketsCollapsed) {
      setCollapsedCategoryIds([]);
      return;
    }
    if (sheetData) {
      setCollapsedCategoryIds(getTopSheetCategoryIds(sheetData));
    }
  };

  const onCategoryFringesClick = (id: any) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(categoryFringesSearchParamName, id);
    setSearchParams(newSearchParams, { replace: true });
  };

  /** Shared by every sheet edit: its budget, this sheet as its view, and how to reload it. */
  const sheetEditScope = () => {
    const summaryKey = budgetKeys.summary(projectId, documentId);
    return {
      scope: sheetScopeRef.current,
      view: saveView,
      refresh: () => queryClient.invalidateQueries({ queryKey: summaryKey }),
    };
  };

  const saveContingency = (update: Partial<BudgetUpdate>, previous: Partial<BudgetUpdate>) => {
    void contingencySaves(queryClient).save({
      ...sheetEditScope(),
      record: "contingency",
      changes: update,
      previous,
      send: async (changes) => {
        await updateBudget.mutateAsync({ update_data: changes });
      },
      show: (values) => updateBudgetDetail(values),
    });
  };

  const saveNewActual = (params: ValueSetterParams) => {
    if (!instanceOfBudgetContingency(params.data)) {
      return false;
    }
    const previousActual = params.data.actual_amount;
    const newValue = toNullableBudgetNumber(params.newValue);
    params.data.actual_amount = newValue;
    params.api.refreshCells({ force: true });
    saveContingency({ contingency_actual: newValue }, { contingency_actual: previousActual });

    return true;
  };

  const saveNewContingencyPercent = (percent: number) => {
    saveContingency(
      { contingency_percentage: Number(percent) },
      { contingency_percentage: budgetDetailRef.current?.contingency_percentage }
    );
  };

  const updateContingencyLock = (locked: boolean) => {
    if (!canEditStructureRef.current) return;
    saveContingency(
      { contingency_locked: locked },
      { contingency_locked: budgetDetailRef.current?.contingency_locked }
    );
  };

  const {
    updateBudgetSubCategory,
    createBudgetCategory,
    updateBudgetCategory,
    deleteBudgetCategory,
    reorderBudgetCategories,
    createBudgetSubCategory,
    updateBudgetSubCategoryStructure,
    deleteBudgetSubCategory,
    reorderBudgetSubCategories,
  } = useBudgetMutations({
    projectId,
    documentId,
    budgetId,
  });

  const patchSheetCategory = (categoryId: number, patch: Partial<BudgetCategory>) => {
    const current = sheetDataRef.current;
    if (!current) return;
    setSheetData(
      current.map((category) =>
        category.budget_category_id === categoryId ? { ...category, ...patch } : category
      )
    );
  };

  const patchSheetSubCategory = (subCategoryId: number, patch: Partial<BudgetSubCategory>) => {
    const current = sheetDataRef.current;
    if (!current) return;
    setSheetData(
      current.map((category) => ({
        ...category,
        sub_categories: category.sub_categories.map((subCategory) =>
          subCategory.budget_sub_category_id === subCategoryId
            ? { ...subCategory, ...patch }
            : subCategory
        ),
      }))
    );
  };

  const handleAddCategory = () => {
    if (!canEditStructure) return;
    createBudgetCategory.mutate(
      { name: "New category" },
      {
        onSuccess: (category) => {
          pendingStructureEditRef.current = { kind: "category", id: category.budget_category_id };
        },
      }
    );
  };

  const handleAddSubCategory = (categoryId: number) => {
    if (!canEditStructure || !sheetDataRef.current) return;
    const category = sheetDataRef.current.find((entry) => entry.budget_category_id === categoryId);
    if (!category) return;

    setCollapsedCategoryIds((current) => current.filter((id) => id !== categoryId));

    const reserved = new Set(collectBudgetAccountNumbers(sheetDataRef.current));
    createBudgetSubCategory.mutate(
      {
        categoryId,
        name: "New sub-category",
        account_number: suggestNextSubCategoryAccountNumber(category, reserved),
      },
      {
        onSuccess: (subCategory) => {
          pendingStructureEditRef.current = {
            kind: "subcategory",
            id: subCategory.budget_sub_category_id,
          };
        },
      }
    );
  };

  const handleMoveCategory = (categoryId: number, direction: -1 | 1) => {
    if (!canEditStructure || !sheetData) return;
    const reordered = moveBudgetCategory(sheetData, categoryId, direction);
    if (!reordered) return;
    reorderBudgetCategories.mutate({ categories: buildCategoryReorderPayload(reordered) });
  };

  const handleMoveSubCategory = (subCategoryId: number, direction: -1 | 1) => {
    if (!canEditStructure || !sheetData) return;

    let reorderedSubs: BudgetSubCategory[] | null = null;
    const parentCategory = sheetData.find((category) =>
      category.sub_categories.some(
        (subCategory) => subCategory.budget_sub_category_id === subCategoryId
      )
    );
    if (!parentCategory) return;

    reorderedSubs = moveBudgetSubCategory(parentCategory.sub_categories, subCategoryId, direction);
    if (!reorderedSubs) return;

    reorderBudgetSubCategories.mutate({
      sub_categories: buildSubCategoryReorderPayload(reorderedSubs),
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmation.id || !deleteConfirmation.kind) return;

    if (deleteConfirmation.kind === "category") {
      deleteBudgetCategory.mutate({ categoryId: deleteConfirmation.id });
    } else {
      deleteBudgetSubCategory.mutate({ subCategoryId: deleteConfirmation.id });
    }

    setDeleteConfirmation({ open: false });
  };

  const startStructureRename = (kind: TopSheetStructureEditTarget["kind"], id: number) => {
    if (!canEditStructureRef.current) return;
    pendingStructureEditRef.current = { kind, id };
    if (isActiveGridApi(topSheetGridApi)) {
      const gridApi = topSheetGridApi;
      requestAnimationFrame(() => {
        if (!isActiveGridApi(gridApi) || !pendingStructureEditRef.current) return;
        const focused = focusTopSheetStructureRename(
          gridApi,
          pendingStructureEditRef.current,
          instanceOfBudgetCategory,
          instanceOfBudgetSubCategory
        );
        if (focused) pendingStructureEditRef.current = null;
      });
    }
  };

  useEffect(() => {
    if (!isActiveGridApi(topSheetGridApi) || !pendingStructureEditRef.current) return;

    const gridApi = topSheetGridApi;
    const frame = requestAnimationFrame(() => {
      if (!isActiveGridApi(gridApi) || !pendingStructureEditRef.current) return;
      const focused = focusTopSheetStructureRename(
        gridApi,
        pendingStructureEditRef.current,
        instanceOfBudgetCategory,
        instanceOfBudgetSubCategory
      );
      if (focused) pendingStructureEditRef.current = null;
    });

    return () => cancelAnimationFrame(frame);
  }, [sheetData, topSheetGridApi, collapsedCategoryIds]);

  cellActionsRef.current = {
    handleAddSubCategory,
    handleMoveCategory,
    handleMoveSubCategory,
    startStructureRename,
    openDeleteConfirmation: (kind, id, name) =>
      setDeleteConfirmation({ open: true, kind, id, name }),
    onTopSheetClick,
    onCategoryFringesClick,
    toggleCategoryCollapsed,
    updateContingencyLock,
  };

  const saveCategoryName = (params: ValueSetterParams) => {
    if (!instanceOfBudgetCategory(params.data) || !canEditStructureRef.current) return false;
    const newName = String(params.newValue ?? "").trim();
    if (!newName || newName === params.data.name) return false;

    // The sheet shows the new name before it saves. A failed save puts the saved name back.
    const categoryId = params.data.budget_category_id;
    const previousName = params.data.name;
    params.data.name = newName;
    void categorySaves(queryClient).save({
      ...sheetEditScope(),
      record: String(categoryId),
      changes: { name: newName },
      previous: { name: previousName },
      send: async (changes) => {
        await updateBudgetCategory.mutateAsync({ categoryId, update: changes });
      },
      show: (values) => patchSheetCategory(categoryId, values),
    });
    return true;
  };

  const saveSubCategoryEdit = (
    subCategoryId: number,
    changes: Partial<SheetSubCategoryFields>,
    previous: Partial<SheetSubCategoryFields>
  ) => {
    void subCategoryStructureSaves(queryClient).save({
      ...sheetEditScope(),
      record: String(subCategoryId),
      changes,
      previous,
      send: async (queued) => {
        await updateBudgetSubCategoryStructure.mutateAsync({ subCategoryId, update: queued });
      },
      show: (values) => patchSheetSubCategory(subCategoryId, values),
    });
  };

  const saveSubCategoryName = (params: ValueSetterParams) => {
    if (
      !instanceOfBudgetSubCategory(params.data) ||
      !canEditStructureRef.current ||
      params.data.locked
    ) {
      return false;
    }
    const newName = String(params.newValue ?? "").trim();
    if (!newName || newName === params.data.name) return false;

    const subCategoryId = params.data.budget_sub_category_id;
    const previousName = params.data.name;
    params.data.name = newName;
    saveSubCategoryEdit(subCategoryId, { name: newName }, { name: previousName });
    return true;
  };

  const saveSubCategoryAccountNumber = (params: ValueSetterParams) => {
    if (
      !instanceOfBudgetSubCategory(params.data) ||
      !canEditStructureRef.current ||
      params.data.locked
    ) {
      return false;
    }
    const newValue = String(params.newValue ?? "").trim();
    if (!newValue || newValue === params.data.account_number) return false;

    const formatError = getBudgetAccountNumberFormatError(newValue);
    if (formatError) return false;

    if (sheetDataRef.current) {
      const normalized = normalizeBudgetAccountNumber(newValue);
      if (normalized) {
        const existing = new Set(
          collectBudgetAccountNumbers(sheetDataRef.current, {
            excludeSubCategoryId: params.data.budget_sub_category_id,
          })
        );
        if (existing.has(normalized)) return false;
      }
    }

    const subCategoryId = params.data.budget_sub_category_id;
    const previousAccountNumber = params.data.account_number;
    params.data.account_number = newValue;
    saveSubCategoryEdit(
      subCategoryId,
      { account_number: newValue },
      { account_number: previousAccountNumber }
    );
    return true;
  };

  const setSubCategoryLocked = (budget_sub_category_id: number, locked: boolean) => {
    const current = sheetDataRef.current;
    if (!current) return;
    setSheetData(
      current.map((category) => ({
        ...category,
        sub_categories: category.sub_categories.map((subCategory) =>
          subCategory.budget_sub_category_id === budget_sub_category_id
            ? {
                ...subCategory,
                locked,
                items: subCategory.items?.map((item) => ({
                  ...item,
                  locked,
                  children: (item.children ?? []).map((child) => ({ ...child, locked })),
                })),
              }
            : subCategory
        ),
      }))
    );
    updateBudgetSubCategory.mutate({ budget_sub_category_id, update: { locked } });

    requestAnimationFrame(() => {
      const gridApi = topSheetGridApiRef.current;
      if (!isActiveGridApi(gridApi)) return;
      gridApi.refreshCells({
        columns: [
          GridColumnIdentifiers.AccountNumber,
          GridColumnIdentifiers.Description,
          GridColumnIdentifiers.RowActions,
        ],
        force: true,
      });
    });
  };
  setSubCategoryLockedRef.current = setSubCategoryLocked;

  const setSubCategoryImportanceScore = (
    budget_sub_category_id: number,
    importanceScore: number
  ) => {
    const current = sheetDataRef.current;
    if (!current) return;
    setSheetData(
      current.map((category) => ({
        ...category,
        sub_categories: category.sub_categories.map((subCategory) =>
          subCategory.budget_sub_category_id === budget_sub_category_id
            ? { ...subCategory, importance: importanceScore }
            : subCategory
        ),
      }))
    );
    updateBudgetSubCategory.mutate({
      budget_sub_category_id,
      update: { importance: importanceScore },
    });
  };

  const [colDefs] = useState<
    ColDef<
      BudgetCategory | BudgetSubCategory | BudgetCategoryFringes | BudgetTotal | BudgetContingency
    >[]
  >([
    {
      headerName: "Acct #",
      field: "account_number",
      headerComponent: GridHeaderWithTooltip,
      headerComponentParams: {
        headerText: "Acct #",
        tooltipText: (
          <span className="text-center">
            Use the lock icon to protect specific budget items during rebudgeting.
            <br />
            Locked items will remain unchanged.
          </span>
        ),
      },
      maxWidth: AccountNumberMaxWidth,
      colId: GridColumnIdentifiers.AccountNumber,
      editable: (params) =>
        canEditStructureRef.current &&
        instanceOfBudgetSubCategory(params.data) &&
        !params.data.locked,
      valueSetter: saveSubCategoryAccountNumber,
      cellClass: (params) =>
        classNames(
          {
            "border-r-0":
              instanceOfBudgetCategory(params.data) || instanceOfBudgetTotal(params.data),
            "pl-6": viewModeRef.current === "outline" && isTopSheetChildRow(params.data),
          },
          "w-full"
        ),
      cellRenderer: (params: any) => {
        const item = params.data;

        if (instanceOfBudgetSubCategory(item)) {
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                !canEditStructureRef.current
                  ? structureDisabledTitleRef.current
                  : item.locked
                    ? budgetNonEditableReasons.lockedAccount
                    : undefined
              }
            >
              <div className="flex h-full w-full min-w-0 items-center gap-1.5">
                <span className="shrink-0 tabular-nums leading-none">{item.account_number}</span>
                <ToggleLock
                  size="sm"
                  locked={item.locked}
                  onLockToggle={() =>
                    setSubCategoryLockedRef.current(item.budget_sub_category_id, !item.locked)
                  }
                />
              </div>
            </CellRendererBase>
          );
        }

        if (instanceOfBudgetContingency(item)) {
          return (
            <CellRendererBase params={params}>
              <div className="w-full" />
            </CellRendererBase>
          );
        }

        return (
          <CellRendererBase
            params={params}
            nonEditableReason={
              instanceOfBudgetCategory(params.data) && !canEditStructureRef.current
                ? structureDisabledTitle
                : budgetNonEditableReasons.accountNumber
            }
          >
            <div className="flex h-full w-full items-center">{item.account_number}</div>
          </CellRendererBase>
        );
      },
    },
    {
      headerName: "Description",
      field: "name",
      editable: (params) => {
        if (!canEditStructureRef.current) return false;
        if (instanceOfBudgetCategory(params.data)) return true;
        if (instanceOfBudgetSubCategory(params.data)) return !params.data.locked;
        return false;
      },
      valueSetter: (params: ValueSetterParams) => {
        if (instanceOfBudgetCategory(params.data)) return saveCategoryName(params);
        if (instanceOfBudgetSubCategory(params.data)) return saveSubCategoryName(params);
        return false;
      },
      cellClass: (params) =>
        classNames("uppercase w-full h-full", {
          "pl-6": viewModeRef.current === "outline" && isTopSheetChildRow(params.data),
        }),
      flex: 1,
      colId: GridColumnIdentifiers.Description,
      cellRenderer: (params: any) => {
        const item = params.data;
        const containsItems = item.items?.length > 0;

        if (instanceOfBudgetCategory(params.data)) {
          const categoryId = params.data.budget_category_id;
          const isCollapsed = params.getCollapsedCategoryIds().includes(categoryId);
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                !canEditStructureRef.current ? structureDisabledTitleRef.current : undefined
              }
            >
              <div
                className="flex h-full w-full cursor-pointer items-center gap-2"
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span className="uppercase">{params.data.name}</span>
              </div>
            </CellRendererBase>
          );
        }

        if (instanceOfBudgetContingency(item)) {
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                !canEditStructureRef.current ? structureDisabledTitleRef.current : undefined
              }
            >
              <div className="flex w-full items-center gap-2">
                <ToggleLock
                  size="sm"
                  locked={item.locked}
                  disabled={!canEditStructureRef.current}
                  onLockToggle={() => cellActionsRef.current.updateContingencyLock(!item.locked)}
                />
                <span className="uppercase">Contingency</span>
              </div>
            </CellRendererBase>
          );
        }

        if (instanceOfBudgetSubCategory(item)) {
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                !canEditStructureRef.current
                  ? structureDisabledTitleRef.current
                  : item.locked
                    ? budgetNonEditableReasons.lockedAccount
                    : undefined
              }
            >
              <div className="flex h-full w-full items-center">
                <div
                  onClick={() =>
                    cellActionsRef.current.onTopSheetClick(item.budget_sub_category_id)
                  }
                  className={classNames(budgetGridLinkClass, "flex min-w-0 flex-1 items-center")}
                >
                  {item.name}
                </div>
              </div>
            </CellRendererBase>
          );
        }

        return (
          <CellRendererBase
            params={params}
            nonEditableReason={
              instanceOfBudgetCategory(params.data) && !canEditStructureRef.current
                ? structureDisabledTitle
                : ""
            }
          >
            <div
              {...(containsItems && {
                onClick: () => cellActionsRef.current.onTopSheetClick(item.budget_sub_category_id),
              })}
              {...(instanceOfBudgetCategoryFringes(item) && {
                onClick: () => cellActionsRef.current.onCategoryFringesClick(item.category_id),
              })}
              className={classNames(
                {
                  [budgetGridLinkClass]:
                    containsItems || instanceOfBudgetCategoryFringes(params.data),
                  "font-bold": instanceOfBudgetTotal(params.data),
                },
                "w-full h-full flex items-center"
              )}
            >
              {item.name}
            </div>
          </CellRendererBase>
        );
      },
      cellRendererParams: {
        getCollapsedCategoryIds: () => collapsedCategoryIdsRef.current,
      },
    },
    {
      hide: true,
      headerName: "Importance",
      colId: GridColumnIdentifiers.ImportanceScore,
      cellClass: "h-full w-full",
      headerComponent: GridHeaderWithTooltip,
      headerComponentParams: {
        headerText: "Importance",
        tooltipText: (
          <span>
            Adjust the importance level (0-10) to prioritize budget allocation.
            <br />
            Higher values direct more budget to essential accounts during re-budgeting.
          </span>
        ),
      },
      cellRenderer: (params: any) => {
        return (
          <CellRendererBase
            params={params}
            nonEditableReason={
              isViewer
                ? budgetNonEditableReasons.viewer
                : params.data.locked
                  ? budgetNonEditableReasons.lockedAccount
                  : budgetNonEditableReasons.importanceOnSubcategory
            }
          >
            <div className={classNames("w-full")}>{params.data.importance}</div>
          </CellRendererBase>
        );
      },
      cellEditor: ImportanceScoreEditor,
      cellEditorParams: {
        setSubCategoryImportanceScore: setSubCategoryImportanceScore,
      },
      singleClickEdit: true,
      editable: (params) =>
        !!instanceOfBudgetSubCategory(params.data!) && !params.data.locked && !isViewer,
    },
    {
      headerName: "$ Estimate",
      colId: GridColumnIdentifiers.Estimate,
      minWidth: TOP_SHEET_MONETARY_COLUMN_WIDTH,
      width: TOP_SHEET_MONETARY_COLUMN_WIDTH,
      suppressSizeToFit: true,
      headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
      cellClass: budgetMonetaryCellClass,
      cellStyle: budgetMonetaryCellStyle,
      cellRenderer: (params: any) => {
        if (instanceOfBudgetTotal(params.data)) {
          const budgetTotalEstimate = getBudgetTotalsFromGrid(params.api).estimate;
          return (
            <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.total}>
              <div className={budgetEstimateAmountClass("w-full font-bold ag-right-aligned-cell")}>
                {formatNumberWithCommas(budgetTotalEstimate, true)}
              </div>
            </CellRendererBase>
          );
        }
        if (instanceOfBudgetContingency(params.data)) {
          const rowData: BudgetCategory[] = [];
          if (params.api) {
            params.api.forEachNode((node: any) => {
              if (instanceOfBudgetCategory(node.data)) {
                rowData.push(node.data);
              }
            });
          }

          const budgetEstimateContingency = getBudgetEstimateContingency(rowData, params.data);
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={budgetNonEditableReasons.contingencyEstimate}
            >
              <div className="w-full">
                <ContingencyAdd
                  percent={params.data.contingency_percentage}
                  flatValue={budgetEstimateContingency}
                  updatePercent={(p: number) => {
                    params.data.contingency_percentage = p;
                    params.api.refreshCells({ force: true });
                  }}
                  onClose={() => {
                    params.saveNewContingencyPercent(params.data.contingency_percentage);
                  }}
                  locked={params.data.locked}
                />
              </div>
            </CellRendererBase>
          );
        }

        return (
          <CellRendererBase
            params={params}
            nonEditableReason={
              isViewer
                ? budgetNonEditableReasons.viewer
                : instanceOfBudgetCategory(params.data)
                  ? budgetNonEditableReasons.total
                  : budgetNonEditableReasons.expandedAccount
            }
          >
            <div className={budgetEstimateAmountClass("w-full ag-right-aligned-cell")}>
              {instanceOfBudgetCategoryFringes(params.data)
                ? formatNumberWithCommas(params.data.estimate, true)
                : instanceOfBudgetTotal(params.data)
                  ? formatNumberWithCommas(params.data.estimate, true)
                  : instanceOfBudgetCategory(params.data)
                    ? formatNumberWithCommas(
                        getBudgetCategoryEstimate(
                          params.data,
                          sheetDataRef.current ?? [params.data]
                        ),
                        true
                      )
                    : formatNumberWithCommas(getBudgetItemsEstimate(params.data.items), true)}
            </div>
          </CellRendererBase>
        );
      },
      cellRendererParams: { saveNewContingencyPercent },
    },
    {
      headerName: "$ Actual",
      field: "actual_amount",
      colId: GridColumnIdentifiers.Actual,
      minWidth: TOP_SHEET_MONETARY_COLUMN_WIDTH,
      width: TOP_SHEET_MONETARY_COLUMN_WIDTH,
      suppressSizeToFit: true,
      headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
      editable: (params) => instanceOfBudgetContingency(params.data) && !isViewer,
      cellClass: budgetMonetaryCellClass,
      cellStyle: budgetMonetaryCellStyle,
      cellRenderer: (params: ICellRendererParams) => {
        if (instanceOfBudgetTotal(params.data)) {
          const budgetTotalActual = getBudgetTotalsFromGrid(params.api).actual;
          return (
            <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.total}>
              <div className={budgetActualAmountClass("font-bold ag-right-aligned-cell w-full")}>
                {formatNumberWithCommas(budgetTotalActual, true)}
              </div>
            </CellRendererBase>
          );
        }

        return (
          <CellRendererBase
            params={params}
            nonEditableReason={
              isViewer
                ? budgetNonEditableReasons.viewer
                : instanceOfBudgetCategory(params.data)
                  ? budgetNonEditableReasons.total
                  : budgetNonEditableReasons.expandedAccount
            }
          >
            <div className={budgetActualAmountClass("w-full ag-right-aligned-cell")}>
              {instanceOfBudgetTotal(params.data) ||
              instanceOfBudgetCategoryFringes(params.data) ||
              instanceOfBudgetContingency(params.data)
                ? formatNumberWithCommas(params.data.actual_amount ?? 0, true)
                : instanceOfBudgetCategory(params.data)
                  ? formatNumberWithCommas(
                      getBudgetCategoryActual(params.data, sheetDataRef.current ?? [params.data]),
                      true
                    )
                  : formatNumberWithCommas(getBudgetItemsActual(params.data.items), true)}
            </div>
          </CellRendererBase>
        );
      },
      valueSetter: saveNewActual,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: {
        min: 0,
        max: MAX_INPUT_NUMBER_VALUE,
      },
    },
    {
      headerName: "$ Variance",
      colId: GridColumnIdentifiers.Variance,
      minWidth: TOP_SHEET_MONETARY_COLUMN_WIDTH,
      width: TOP_SHEET_MONETARY_COLUMN_WIDTH,
      suppressSizeToFit: true,
      headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
      cellClass: budgetMonetaryCellClass,
      cellStyle: budgetMonetaryCellStyle,
      cellRenderer: (params: ICellRendererParams) => {
        if (instanceOfBudgetTotal(params.data)) {
          const budgetTotalVariance = getBudgetTotalsFromGrid(params.api).variance;
          return (
            <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.total}>
              <div
                className={budgetVarianceAmountClass(
                  budgetTotalVariance,
                  "w-full font-bold ag-right-aligned-cell"
                )}
              >
                {formatNumberWithCommas(budgetTotalVariance, true)}
              </div>
            </CellRendererBase>
          );
        }
        if (instanceOfBudgetContingency(params.data)) {
          const rowData: BudgetCategory[] = [];
          if (params.api) {
            params.api.forEachNode((node: any) => {
              if (instanceOfBudgetCategory(node.data)) {
                rowData.push(node.data);
              }
            });
          }

          const variance = getBudgetContingencyVariance(rowData, params.data);
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                isViewer
                  ? budgetNonEditableReasons.viewer
                  : instanceOfBudgetCategory(params.data)
                    ? budgetNonEditableReasons.total
                    : budgetNonEditableReasons.expandedAccount
              }
            >
              <div className={budgetVarianceAmountClass(variance, "w-full ag-right-aligned-cell")}>
                {formatNumberWithCommas(variance ?? 0, true)}
              </div>
            </CellRendererBase>
          );
        }

        const variance =
          instanceOfBudgetTotal(params.data) || instanceOfBudgetCategoryFringes(params.data)
            ? params.data.variance
            : instanceOfBudgetCategory(params.data)
              ? getBudgetCategoryVariance(params.data, sheetDataRef.current ?? [params.data])
              : getBudgetItemsVariance(params.data.items);

        return (
          <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.variance}>
            <div className={budgetVarianceAmountClass(variance, "w-full ag-right-aligned-cell")}>
              {formatNumberWithCommas(variance ?? 0, true)}
            </div>
          </CellRendererBase>
        );
      },
    },
    {
      headerName: "",
      colId: GridColumnIdentifiers.RowActions,
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      pinned: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
      },
      cellClass: "budget-row-actions-cell w-full h-full",
      cellRenderer: (params: ICellRendererParams) => {
        const item = params.data;
        if (!canEditStructureRef.current || !item) return null;

        if (instanceOfBudgetCategory(item)) {
          const categoryId = item.budget_category_id;
          return (
            <div
              className="budget-row-actions flex h-full w-full items-center justify-center"
              onClick={(event) => event.stopPropagation()}
            >
              <TopSheetRowActions
                rowKind="category"
                disabled={!canEditStructureRef.current}
                disabledTitle={structureDisabledTitleRef.current}
                canMoveUp={canMoveBudgetCategoryUp(sheetDataRef.current ?? [], categoryId)}
                canMoveDown={canMoveBudgetCategoryDown(sheetDataRef.current ?? [], categoryId)}
                onAddSubCategory={() => cellActionsRef.current.handleAddSubCategory(categoryId)}
                onRename={() => cellActionsRef.current.startStructureRename("category", categoryId)}
                onMoveUp={() => cellActionsRef.current.handleMoveCategory(categoryId, -1)}
                onMoveDown={() => cellActionsRef.current.handleMoveCategory(categoryId, 1)}
                onDelete={() =>
                  cellActionsRef.current.openDeleteConfirmation("category", categoryId, item.name)
                }
              />
            </div>
          );
        }

        if (instanceOfBudgetSubCategory(item)) {
          const subCategoryId = item.budget_sub_category_id;
          const parentCategory = sheetDataRef.current?.find((category) =>
            category.sub_categories.some(
              (subCategory) => subCategory.budget_sub_category_id === subCategoryId
            )
          );
          const siblingSubCategories = parentCategory?.sub_categories ?? [];

          return (
            <div
              className="budget-row-actions flex h-full w-full items-center justify-center"
              onClick={(event) => event.stopPropagation()}
            >
              <TopSheetRowActions
                rowKind="subcategory"
                disabled={!canEditStructureRef.current || item.locked}
                disabledTitle={
                  item.locked
                    ? budgetNonEditableReasons.lockedAccount
                    : structureDisabledTitleRef.current
                }
                canMoveUp={canMoveBudgetSubCategoryUp(siblingSubCategories, subCategoryId)}
                canMoveDown={canMoveBudgetSubCategoryDown(siblingSubCategories, subCategoryId)}
                onRename={() =>
                  cellActionsRef.current.startStructureRename("subcategory", subCategoryId)
                }
                onMoveUp={() => cellActionsRef.current.handleMoveSubCategory(subCategoryId, -1)}
                onMoveDown={() => cellActionsRef.current.handleMoveSubCategory(subCategoryId, 1)}
                onDelete={() =>
                  cellActionsRef.current.openDeleteConfirmation(
                    "subcategory",
                    subCategoryId,
                    item.name
                  )
                }
              />
            </div>
          );
        }

        return null;
      },
    },
  ]);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <InputV3
          type="search"
          value={quickFilter}
          onChange={(event) => setQuickFilter(event.target.value)}
          placeholder="Filter sub-categories by name or number"
          aria-label="Filter budget sub-categories"
          className="w-full max-w-xs"
        />
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghostBrand"
                onClick={handleAddCategory}
                disabled={!canEditStructure || createBudgetCategory.isPending}
                aria-label="Add category"
                aria-hidden={!canEditStructure}
                className={!canEditStructure ? "pointer-events-none invisible" : undefined}
              >
                <Plus />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add category</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghostSecondary"
                onClick={handleToggleAllBuckets}
                disabled={!sheetData?.length}
                aria-label={allBucketsCollapsed ? "Expand all" : "Collapse all"}
                title={allBucketsCollapsed ? "Expand all" : "Collapse all"}
              >
                {allBucketsCollapsed ? <UnfoldVertical /> : <FoldVertical />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{allBucketsCollapsed ? "Expand all" : "Collapse all"}</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className={classNames(RIVET_BUDGET_TOP_SHEET_WRAPPER_CLASS, "min-h-0 flex-1")}>
        <div
          className={classNames(
            "ag-theme-quartz",
            RIVET_BUDGET_GRID_CLASS,
            RIVET_BUDGET_TOP_SHEET_GRID_CLASS,
            RIVET_BUDGET_TOP_SHEET_GRID_MARKER_CLASS,
            "min-w-0"
          )}
          role="region"
          aria-label="Budget top sheet"
          style={{
            width: "100%",
            height: "100%",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <AgGridReact
            ref={gridRef}
            columnDefs={colDefs}
            rowData={bodyRows ?? undefined}
            pinnedBottomRowData={pinnedRows}
            getRowId={getTopSheetRowId}
            getRowClass={getTopSheetRowClass}
            getRowStyle={getRowStyle}
            rowHeight={TOP_SHEET_ROW_HEIGHT}
            defaultColDef={topSheetDefaultColDef}
            suppressScrollOnNewData
            animateRows={false}
            onGridReady={(params) => setTopSheetGridApi(params.api)}
            onGridPreDestroyed={() => setTopSheetGridApi(undefined)}
            onCellClicked={handleTopSheetCellClicked}
            loadingOverlayComponent={AgGridLoaderOverlay}
          />
        </div>
      </div>
      <ConfirmationModal
        title={
          deleteConfirmation.kind === "category"
            ? "Delete this category?"
            : "Delete this sub-category?"
        }
        isOpen={deleteConfirmation.open}
        confirmButtonProps={{
          text: "Delete",
          icon: FaTrash,
          onClick: handleConfirmDelete,
        }}
        cancelButtonProps={{
          text: "Cancel",
          onClick: () => setDeleteConfirmation({ open: false }),
        }}
      >
        <p className="text-sm text-muted-foreground">
          {deleteConfirmation.kind === "category" ? (
            <>
              Deleting <strong>{deleteConfirmation.name}</strong> removes all sub-categories and
              line items inside it. This cannot be undone.
            </>
          ) : (
            <>
              Deleting <strong>{deleteConfirmation.name}</strong> removes all line items in this
              sub-category. This cannot be undone.
            </>
          )}
        </p>
      </ConfirmationModal>
    </div>
  );
};
